import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAgentsApi } from '@/hooks/useAgentsApi';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  Copy,
  ExternalLink,
  Loader2,
  MapPin,
  Percent,
  ShoppingBag,
  User,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export default function AgentProfile() {
  const agentsApi = useAgentsApi();
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-profile'],
    queryFn: async () => {
      const response = await agentsApi.getProfile();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const handleCopyLink = async () => {
    const referralLink = `${window.location.origin}/register?role=affiliate_owner&agent=${data?.agent?.shopify_affiliate_code || ''}`;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyShopifyCode = async () => {
    try {
      await navigator.clipboard.writeText(data?.agent?.shopify_affiliate_code || '');
      toast.success('Shopify code copied!');
    } catch {
      toast.error('Failed to copy code');
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
          <p className="text-destructive">Failed to load profile</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const agent = data?.agent;
  const referralLink = `${window.location.origin}/register?role=affiliate_owner&agent=${agent?.shopify_affiliate_code || ''}`;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Agent Profile</h1>
        <p className="mt-1 text-muted-foreground">
          View your profile details and referral information.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Profile Info */}
        <Card className="animate-fade-in">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Profile Information
            </CardTitle>
            <CardDescription>Your agent account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20 text-2xl font-bold text-primary">
                {agent?.name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div>
                <h3 className="text-xl font-semibold">{agent?.name}</h3>
                <p className="text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {agent?.territory || 'No territory assigned'}
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-border">
              <div>
                <Label className="text-muted-foreground">Territory</Label>
                <p className="text-lg font-medium">{agent?.territory || 'Not assigned'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Commission Rates */}
        <Card className="animate-fade-in" style={{ animationDelay: '50ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-primary" />
              Commission Rates
            </CardTitle>
            <CardDescription>Your earning rates by type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <ShoppingBag className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Equipment Sales</p>
                    <p className="text-sm text-muted-foreground">Shopify orders</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-primary">
                  {((agent?.commission_rate_equipment || 0) * 100).toFixed(0)}%
                </p>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-green-500/10 p-2">
                    <Percent className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="font-medium">Subscription Commission</p>
                    <p className="text-sm text-muted-foreground">Monthly recurring</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-green-500">
                  {((agent?.commission_rate_subs || 0) * 100).toFixed(0)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Link */}
        <Card className="lg:col-span-2 animate-fade-in" style={{ animationDelay: '100ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-primary" />
              Referral Links
            </CardTitle>
            <CardDescription>
              Share these links to sign up affiliates and earn commission
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Affiliate Referral Link */}
            <div className="space-y-2">
              <Label>Affiliate Signup Link</Label>
              <div className="flex gap-2">
                <Input value={referralLink} readOnly className="font-mono text-sm" />
                <Button onClick={handleCopyLink} variant="outline" className="shrink-0">
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Share this link with gym owners. When they sign up, they'll be automatically
                attributed to you.
              </p>
            </div>

            {/* Stripe Payment Links */}
            <div className="space-y-4">
              <Label>Direct Purchase Links (Stripe)</Label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={`https://buy.stripe.com/9B27sMbcW1o7fApcTfcIE02${agent?.shopify_affiliate_code ? `?prefilled_promo_code=${agent.shopify_affiliate_code}` : ''}`}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    onClick={async () => {
                      const link = `https://buy.stripe.com/9B27sMbcW1o7fApcTfcIE02${agent?.shopify_affiliate_code ? `?prefilled_promo_code=${agent.shopify_affiliate_code}` : ''}`;
                      await navigator.clipboard.writeText(link);
                      toast.success('Affiliate payment link copied!');
                    }}
                    variant="outline"
                    className="shrink-0"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Affiliate Plan (£67.15/mo) - For gym owners
                </p>

                <div className="flex gap-2">
                  <Input
                    value={`https://buy.stripe.com/28E5kE4Oyd6P1Jz6uRcIE01${agent?.shopify_affiliate_code ? `?prefilled_promo_code=${agent.shopify_affiliate_code}` : ''}`}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    onClick={async () => {
                      const link = `https://buy.stripe.com/28E5kE4Oyd6P1Jz6uRcIE01${agent?.shopify_affiliate_code ? `?prefilled_promo_code=${agent.shopify_affiliate_code}` : ''}`;
                      await navigator.clipboard.writeText(link);
                      toast.success('Individual payment link copied!');
                    }}
                    variant="outline"
                    className="shrink-0"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Individual Plan ($6.75/mo) - For personal users
                </p>
              </div>
            </div>

            {/* Shopify Affiliate Code */}
            <div className="space-y-2">
              <Label>Shopify Equipment Code</Label>
              <div className="flex gap-2">
                <Input
                  value={agent?.shopify_affiliate_code || 'Not assigned'}
                  readOnly
                  className="font-mono text-sm"
                />
                {agent?.shopify_affiliate_code && (
                  <Button onClick={handleCopyShopifyCode} variant="outline" className="shrink-0">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Your unique code for Shopify equipment purchases. Customers can use this at
                checkout.
              </p>
            </div>

            {/* Info Box */}
            <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
              <h4 className="font-medium text-primary mb-2">How it works</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>1. Share your referral link with potential gym owners</li>
                <li>2. When they register, they're automatically linked to your account</li>
                <li>3. You earn commission on their subscription and equipment purchases</li>
                <li>4. Track your earnings in the Commission Ledger</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
